
import QTree from "quasar/src/components/tree/QTree";

export default {
    name: "QfTree",
    mixins: [QTree],
    data() {
        return {
            cursor: null,
            cursorIndex: null
        };
    },
    computed: {
        meta() {
            const meta = {};

            const travel = (node, parent) => {
                const tickStrategy = node.tickStrategy || (parent ? parent.tickStrategy : this.tickStrategy);
                const
                    key = node[this.nodeKey];
                const isParent = node.children && node.children.length > 0;
                const isLeaf = !isParent;
                const selectable = !node.disabled && this.hasSelection && node.selectable !== false;
                const expandable = !node.disabled && node.expandable !== false;
                const hasTicking = tickStrategy !== "none";
                const strictTicking = tickStrategy === "strict";
                const leafFilteredTicking = tickStrategy === "leaf-filtered";
                const leafTicking = tickStrategy === "leaf" || tickStrategy === "leaf-filtered";

                let tickable = !node.disabled && node.tickable !== false;
                if (leafTicking && tickable && parent && !parent.tickable) {
                    tickable = false;
                }

                let { lazy } = node;
                if (lazy && this.lazy[key]) {
                    lazy = this.lazy[key];
                }

                const m = {
                    key,
                    parent,
                    isParent,
                    isLeaf,
                    lazy,
                    disabled: node.disabled,
                    link: selectable || (expandable && (isParent || lazy === true)),
                    children: [],
                    matchesFilter: this.filter ? this.filterMethod(node, this.filter) : true,

                    selected: key === this.selected && selectable,
                    hasCursor: key === this.cursor,
                    selectable,
                    expanded: isParent ? this.innerExpanded.includes(key) : false,
                    expandable,
                    noTick: node.noTick || (!strictTicking && lazy && lazy !== "loaded"),
                    tickable,
                    tickStrategy,
                    hasTicking,
                    strictTicking,
                    leafFilteredTicking,
                    leafTicking,
                    ticked: strictTicking
                        ? this.innerTicked.includes(key)
                        : (isLeaf ? this.innerTicked.includes(key) : false)
                };

                meta[key] = m;

                if (isParent) {
                    m.children = node.children.map(n => travel(n, m));

                    if (this.filter) {
                        if (!m.matchesFilter) {
                            m.matchesFilter = m.children.some(n => n.matchesFilter);
                        }
                        if (
                            m.matchesFilter
                                && !m.noTick
                                && !m.disabled
                                && m.tickable
                                && leafFilteredTicking
                                && m.children.every(n => !n.matchesFilter || n.noTick || !n.tickable)
                        ) {
                            m.tickable = false;
                        }
                    }

                    if (m.matchesFilter) {
                        if (!m.noTick && !strictTicking && m.children.every(n => n.noTick)) {
                            m.noTick = true;
                        }

                        if (leafTicking) {
                            m.ticked = false;
                            m.indeterminate = m.children.some(node => node.indeterminate);

                            if (!m.indeterminate) {
                                const sel = m.children
                                    .reduce((acc, meta) => (meta.ticked ? acc + 1 : acc), 0);

                                if (sel === m.children.length) {
                                    m.ticked = true;
                                } else if (sel > 0) {
                                    m.indeterminate = true;
                                }
                            }
                        }
                    }
                }

                return m;
            };

            this.nodes.forEach(node => travel(node, null));
            return meta;
        }
    },
    created() {
        if (this.defaultExpandAll) {
            this.expandAll();
        }
    },
    methods: {
        __getNode(h, node) {
            const
                key = node[this.nodeKey];
            const meta = this.meta[key];
            const header = node.header
                ? this.$scopedSlots[`header-${node.header}`] || this.$scopedSlots["default-header"]
                : this.$scopedSlots["default-header"];

            const children = meta.isParent ? this.__getChildren(h, node.children) : [];

            const isParent = children.length > 0 || (meta.lazy && meta.lazy !== "loaded");

            let body = node.body
                ? this.$scopedSlots[`body-${node.body}`] || this.$scopedSlots["default-body"] : this.$scopedSlots["default-body"];
            const slotScope = header || body ? this.__getSlotScope(node, meta, key) : null;

            if (body) {
                body = h("div", { staticClass: "q-tree-node-body relative-position" }, [
                    h("div", { class: this.contentClass }, [
                        body(slotScope)
                    ])
                ]);
            }

            return h("div", {
                key,
                staticClass: "q-tree-node",
                class: { "q-tree-node-parent": isParent, "q-tree-node-child": !isParent }
            }, [
                h("div", {
                    staticClass: "q-tree-node-header relative-position row no-wrap items-center",
                    class: {
                        "q-tree-node-link": meta.link,
                        "q-tree-node-selected": meta.selected,
                        "q-tree-node-cursor": meta.hasCursor,
                        disabled: meta.disabled
                    },
                    on: { click: () => { this.__onClick(node, meta); } },
                    directives: process.env.THEME === "mat" && meta.selectable ? [{ name: "ripple" }] : null
                }, [
                    meta.lazy === "loading"
                        ? h("q-spinner", {
                            staticClass: "q-tree-node-header-media q-mr-xs",
                            props: { color: this.computedControlColor }
                        })
                        : (
                            isParent
                                ? h("q-icon", {
                                    staticClass: "q-tree-arrow q-mr-xs transition-generic",
                                    class: { "q-tree-arrow-rotate": meta.expanded },
                                    props: { name: this.computedIcon },
                                    nativeOn: {
                                        click: e => {
                                            this.__onExpandClick(node, meta, e);
                                        }
                                    }
                                })
                                : null
                        ),

                    h("span", { staticClass: "row no-wrap items-center", class: this.contentClass }, [
                        meta.hasTicking && !meta.noTick
                            ? h("q-checkbox", {
                                staticClass: "q-mr-xs",
                                props: {
                                    value: meta.indeterminate ? null : meta.ticked,
                                    color: this.computedControlColor,
                                    dark: this.dark,
                                    keepColor: true,
                                    disable: !meta.tickable
                                },
                                on: {
                                    input: v => {
                                        this.__onTickedClick(node, meta, v);
                                    }
                                }
                            })
                            : null,
                        header
                            ? header(slotScope)
                            : [
                                this.__getNodeMedia(h, node),
                                h("span", node[this.labelKey])
                            ]
                    ])
                ]),

                isParent
                    ? h("q-slide-transition", {
                        props: { duration: this.duration }
                    }, [
                        h("div", {
                            directives: [{ name: "show", value: meta.expanded }],
                            staticClass: "q-tree-node-collapsible",
                            class: `text-${this.color}`
                        }, [
                            body,
                            h("div", {
                                staticClass: "q-tree-children",
                                class: { disabled: meta.disabled }
                            }, children)
                        ])
                    ])
                    : body
            ]);
        },
        getSiblings(start = this.cursor) {
            let siblings;
            if (start) {
                const { parent } = this.meta[start];
                if (parent) {
                    siblings = this.meta[parent.key].children;
                } else {
                    siblings = this.nodes;
                }
            } else {
                siblings = null;
            }

            return siblings;
        },
        getNextAvailable() {
            let done = false; let siblings; let index = -1; let { cursor } = this; let
                failSafe = 0;

            if (cursor) {
                while (!done) {
                    const { parent } = this.meta[cursor];

                    if (parent) {
                        const parentSiblings = this.getSiblings(parent.key);
                        const parentIndex = parentSiblings.findIndex(element => element.key === parent.key);

                        if (parentIndex < (parentSiblings.length - 1)) {
                            index = parentIndex + 1;
                            siblings = parentSiblings;
                            done = true;
                        } else {
                            cursor = parent.key;
                            failSafe++;
                            if (failSafe > 10) {
                                alert("failsafe");
                                done = true;
                            }
                        }
                    } else {
                        done = true;
                    }
                }
            }

            return { index, siblings };
        },
        getPrevAvailable() {
            let siblings = []; let index = -1; let
                currentCursor = this.cursor;

            if (currentCursor) {
                siblings = this.getSiblings(currentCursor);
                index = siblings.findIndex(element => element.key === currentCursor);

                if (index > 0) {
                    index -= 1;
                    currentCursor = siblings[index].key;

                    while (this.meta[currentCursor].isParent && this.meta[currentCursor].expanded) {
                        index = this.meta[currentCursor].children.length - 1;
                        siblings = this.meta[currentCursor].children;

                        currentCursor = this.meta[currentCursor].children[index].key;
                    }
                } else {
                    const { parent } = this.meta[currentCursor];
                    if (parent) {
                        siblings = this.getSiblings(parent.key);
                        index = siblings.findIndex(element => element.key === parent.key);
                        currentCursor = parent.key;
                    }
                }
            }

            return { index, siblings };
        },
        moveDown() {
            if (this.cursor) {
                const { children, expanded } = this.meta[this.cursor];
                if (children.length && expanded) {
                    this.cursorIndex = 0;
                    this.cursor = children[this.cursorIndex].key;
                } else {
                    const siblings = this.getSiblings();
                    if (this.cursorIndex < siblings.length - 1) {
                        this.cursorIndex++;
                        this.cursor = siblings[this.cursorIndex].key;
                    } else {
                        const { index, siblings } = this.getNextAvailable();
                        if (index !== -1) {
                            this.cursorIndex = index;
                            this.cursor = siblings[this.cursorIndex].key;
                        }
                    }
                }
            } else {
                this.cursorIndex = 0;
                this.cursor = this.nodes[this.cursorIndex].key;
            }
        },
        moveUp() {
            if (this.cursor) {
                const { index, siblings } = this.getPrevAvailable();
                if (index !== -1) {
                    this.cursorIndex = index;
                    this.cursor = siblings[this.cursorIndex].key;
                }
            } else {
                this.cursorIndex = this.nodes.length - 1;
                this.cursor = this.nodes[this.cursorIndex].key;
            }
        },
        keyExpand() {
            if (this.cursor) {
                const { isParent, expanded, disabled } = this.meta[this.cursor];
                if (isParent && !expanded && !disabled) {
                    this.__setExpanded(this.cursor, true);
                }
            }
        },
        keyCollapse() {
            if (this.cursor) {
                const { isParent, expanded } = this.meta[this.cursor];
                if (isParent && expanded) {
                    this.__setExpanded(this.cursor, false);
                } else {
                    const { parent } = this.meta[this.cursor];
                    if (parent) {
                        const siblings = this.getSiblings(parent.key);
                        this.cursorIndex = siblings.findIndex(element => element.key === parent.key);
                        this.cursor = parent.key;
                    }
                }
            }
        },
        setCursor(key) {
            const siblings = this.getSiblings(key);
            this.cursor = key;
            this.cursorIndex = siblings.findIndex(element => element.key === this.cursor);
        },
        getCursor() {
            return this.cursor;
        }
    },
    render(h) {
        const children = this.__getChildren(h, this.nodes);

        return h(
            "div", {
                staticClass: "q-tree qf-tree relative-position",
                class: this.classes
            },
            children.length === 0 ? (this.filter ? this.noResultsLabel || this.$q.i18n.tree.noResults : this.noNodesLabel || this.$q.i18n.tree.noNodes) : children
        );
    }
};
